import axios from 'axios';
import { Config } from '../config';

/**
 * Health Check Service
 * Periodically pings the backend /health endpoint to prevent it from becoming inactive
 */

let healthCheckInterval: NodeJS.Timeout | null = null;
let isBackendHealthy = false;
let isInitialized = false; // Track if health check has been started
const HEALTH_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes when backend is healthy
const HEALTH_CHECK_INTERVAL_OFFLINE = 2 * 1000; // 2 seconds when backend is offline (aggressive retry)

// Listeners for health status changes
const statusListeners: ((isHealthy: boolean) => void)[] = [];

/**
 * Subscribe to health status changes
 */
export const subscribeToHealthStatus = (callback: (isHealthy: boolean) => void): (() => void) => {
  statusListeners.push(callback);
  // Return unsubscribe function
  return () => {
    const index = statusListeners.indexOf(callback);
    if (index > -1) {
      statusListeners.splice(index, 1);
    }
  };
};

/**
 * Get current backend health status
 */
export const getBackendHealth = (): boolean => {
  return isBackendHealthy;
};

/**
 * Notify all listeners of health status change
 */
const notifyStatusChange = (isHealthy: boolean): void => {
  if (isBackendHealthy !== isHealthy) {
    isBackendHealthy = isHealthy;
        
    // Adjust polling interval based on health status
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval);
      
      // When offline, check more aggressively to bring it back online
      const nextInterval = isHealthy ? HEALTH_CHECK_INTERVAL : HEALTH_CHECK_INTERVAL_OFFLINE;
      
      healthCheckInterval = setInterval(() => {
        performHealthCheck();
      }, nextInterval);
      
    }
    
    // Notify listeners
    statusListeners.forEach(callback => {
      try {
        callback(isHealthy);
      } catch (error) {
        console.error('Error in health status listener:', error);
      }
    });
  }
};

/**
 * Start the health check service
 * Pings the backend /health endpoint at regular intervals
 * When backend is offline, increases poll frequency to 5 seconds
 */
export const startHealthCheck  = (): void => {
  if (isInitialized) {
    return;
  }
  
  isInitialized = true;
  
  // Perform initial health check
  performHealthCheck();
  
  // Set up periodic health checks - will adjust interval based on health status
  healthCheckInterval = setInterval(() => {
    performHealthCheck();
  }, HEALTH_CHECK_INTERVAL);
};

/**
 * Stop the health check service
 */
export const stopHealthCheck = (): void => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
  }
  isInitialized = false;
};

/**
 * Perform a single health check
 */
const performHealthCheck = async (): Promise<void> => {
  try {
    await axios.get(`${Config.apiUrl}/health`, {
      timeout: 5000,
    });
    notifyStatusChange(true);
  } catch (error) {
    notifyStatusChange(false);
    // Don't throw - health check failures shouldn't crash the app
  }
};

const healthCheckService = {
  startHealthCheck,
  stopHealthCheck,
  getBackendHealth,
  subscribeToHealthStatus,
};

export default healthCheckService;
